import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, IconButton, Dialog, DialogTitle,
    DialogContent, TextField, MenuItem, Button, Divider, Stack, Tooltip
} from '@mui/material';
import {
    format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
    addDays, isSameMonth, isSameDay, addMonths, subMonths
} from 'date-fns';
import { ChevronLeft, ChevronRight, Business, EventAvailable, Delete } from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const HRCalendarManager = () => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [holidays, setHolidays] = useState([]);
    const [branches, setBranches] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        description: '',
        branchId: '',
        statusType: 'PAID_HOLIDAY'
    });

    const loadData = useCallback(async () => {
        try {
            // Corrected paths: removed /api prefix because axios baseURL already includes it
            const [bRes, hRes] = await Promise.all([
                api.get('/branches'),
                api.get('/holidays')
            ]);
            setBranches(bRes.data || []);
            setHolidays(hRes.data || []);
        } catch (err) {
            toast.error('Failed to load calendar data');
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData, currentMonth]);

    const handleOpenDialog = (date, existingHoliday) => {
        setSelectedDate(date);
        const isSunday = date.getDay() === 0;
        
        setFormData({
            description: existingHoliday?.description || (isSunday ? 'Sunday Working Day' : ''),
            branchId: existingHoliday?.branchId || '',
            statusType: existingHoliday?.holidayType || (isSunday ? 'WORKING_DAY' : 'PAID_HOLIDAY')
        });
        setOpen(true);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // FIX: Changed path from '/api/holidays' to '/holidays' to avoid 404
            await api.post('/holidays', {
                date: format(selectedDate, 'yyyy-MM-dd'),
                branchId: formData.branchId || null,
                description: formData.description || (formData.statusType === 'WORKING_DAY' ? 'Working Day' : 'Holiday'),
                holidayType: formData.statusType 
            });
            toast.success('Schedule Updated');
            setOpen(false);
            loadData(); 
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error saving changes');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (holidayId) => {
        if (!window.confirm("Remove this override and revert to default?")) return;
        try {
            // FIX: Changed path to '/holidays/...'
            await api.delete(`/holidays/${holidayId}`);
            toast.success('Reverted to default');
            setOpen(false);
            loadData();
        } catch {
            toast.error('Failed to remove');
        }
    };

    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days = [];
    let day = startDate;
    while (day <= endDate) {
        days.push(day);
        day = addDays(day, 1);
    }

    return (
        <Box sx={{ p: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Paper sx={{ p: 4, borderRadius: 4, boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ p: 1, bgcolor: '#dbeafe', borderRadius: 2 }}>
                            <EventAvailable sx={{ color: '#2563eb' }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight={800} color="#1e293b">Company Calendar</Typography>
                            <Typography variant="body2" color="text.secondary">Manage holidays and Sunday overrides</Typography>
                        </Box>
                    </Stack>

                    <Stack direction="row" alignItems="center" sx={{ bgcolor: '#f1f5f9', borderRadius: 3, p: 0.5 }}>
                        <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}><ChevronLeft /></IconButton>
                        <Typography fontWeight={700} sx={{ minWidth: 140, textAlign: 'center' }}>
                            {format(currentMonth, 'MMMM yyyy')}
                        </Typography>
                        <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}><ChevronRight /></IconButton>
                    </Stack>
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 1 }}>
                    {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(d => (
                        <Typography key={d} align="center" fontWeight={800} fontSize={12} color={d === 'SUN' ? 'error.main' : 'text.secondary'} sx={{ letterSpacing: 1 }}>
                            {d}
                        </Typography>
                    ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridAutoRows: '140px', border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                    {days.map(d => {
                        const formattedDate = format(d, 'yyyy-MM-dd');
                        const isCurrentMonth = isSameMonth(d, monthStart);
                        const holiday = holidays.find(h => h.date === formattedDate);
                        
                        let bgColor = '#ffffff';
                        if (!isCurrentMonth) bgColor = '#f8fafc';
                        else if (holiday?.holidayType === 'WORKING_DAY') bgColor = '#f0fdf4'; 
                        else if (holiday) bgColor = '#eff6ff';
                        else if (d.getDay() === 0) bgColor = '#fff1f2'; 

                        return (
                            <Box
                                key={d.toString()}
                                onClick={() => isCurrentMonth && handleOpenDialog(d, holiday)}
                                sx={{
                                    p: 1.5,
                                    borderRight: '1px solid #e2e8f0',
                                    borderBottom: '1px solid #e2e8f0',
                                    bgcolor: bgColor,
                                    cursor: isCurrentMonth ? 'pointer' : 'default',
                                    '&:hover': { bgcolor: isCurrentMonth ? '#f1f5f9' : bgColor }
                                }}
                            >
                                {isCurrentMonth && (
                                    <Stack justifyContent="space-between" height="100%">
                                        <Typography 
                                            fontSize={14}
                                            fontWeight={isSameDay(d, new Date()) ? 900 : 700} 
                                            color={d.getDay() === 0 && holiday?.holidayType !== 'WORKING_DAY' ? 'error.main' : 'text.primary'}
                                        >
                                            {format(d, 'd')}
                                        </Typography>

                                        {holiday && (
                                            <Tooltip title={holiday.description}>
                                                <Box sx={{ 
                                                    p: 0.8, 
                                                    bgcolor: holiday.holidayType === 'WORKING_DAY' ? '#16a34a' : '#2563eb', 
                                                    color: '#fff', borderRadius: 1.5, textAlign: 'center',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                }}>
                                                    <Typography fontSize={10} fontWeight={800} noWrap>
                                                        {holiday.holidayType === 'WORKING_DAY' ? 'WORKING SUN' : holiday.description}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Paper>

            <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { borderRadius: 4, width: 400 } }}>
                <DialogTitle sx={{ fontWeight: 800, pt: 3 }}>
                    {formData.statusType === 'WORKING_DAY' ? '⚡ Working Day Override' : '📅 Set Holiday'}
                    <Typography variant="body2" color="text.secondary">
                        {selectedDate && format(selectedDate, 'eeee, MMMM do')}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1, pb: 2 }}>
                        <TextField
                            select label="Schedule Type" fullWidth
                            value={formData.statusType}
                            onChange={e => setFormData({ ...formData, statusType: e.target.value })}
                        >
                            <MenuItem value="PAID_HOLIDAY">Paid Holiday</MenuItem>
                            <MenuItem value="WORKING_DAY">Working Day (Override)</MenuItem>
                        </TextField>

                        <TextField
                            label="Reason / Note" fullWidth
                            placeholder="e.g. Diwali, or Urgent Project Day"
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                        />

                        <TextField
                            select label="Branch Scope" fullWidth
                            value={formData.branchId}
                            onChange={e => setFormData({ ...formData, branchId: e.target.value })}
                        >
                            <MenuItem value="">Global (All Branches)</MenuItem>
                            {branches.map(b => (
                                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                            ))}
                        </TextField>

                        <Stack direction="row" spacing={1}>
                            <Button 
                                variant="contained" 
                                fullWidth 
                                onClick={handleSave} 
                                disabled={loading}
                                sx={{ borderRadius: 2, py: 1.5, fontWeight: 800 }}
                            >
                                {loading ? 'Saving...' : 'Apply Change'}
                            </Button>
                            
                            {selectedDate && holidays.find(h => h.date === format(selectedDate, 'yyyy-MM-dd')) && (
                                <IconButton 
                                    color="error" 
                                    onClick={() => handleDelete(holidays.find(h => h.date === format(selectedDate, 'yyyy-MM-dd')).id)}
                                    sx={{ bgcolor: '#fee2e2', borderRadius: 2 }}
                                >
                                    <Delete />
                                </IconButton>
                            )}
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default HRCalendarManager;